import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { CATEGORIES, waLink } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const reveal = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Products() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((res) => setProducts(res.data))
      .catch((e) => console.error("Failed to load products", e));
  }, []);

  return (
    <section
      id="products"
      className="relative z-10 border-t border-white/10 py-28 lg:py-40"
      data-testid="products-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="mb-20 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-xl">
            <p className="overline mb-6">Our Portfolio</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[1]">
              Three categories. One standard of quality.
            </h2>
          </div>
          <p className="max-w-sm text-white/50">
            Explore the brands we supply. Prices are shared on request based on
            grade, pack size and order volume.
          </p>
        </div>

        <div className="space-y-24">
          {CATEGORIES.map((cat, ci) => {
            const items = products.filter((p) => p.category === cat.id);
            const flip = ci % 2 === 1;
            return (
              <div
                key={cat.id}
                className="grid grid-cols-1 gap-8 lg:grid-cols-12"
                data-testid={`category-block-${cat.id}`}
              >
                {/* Visual */}
                <motion.div
                  variants={reveal}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  className={`relative lg:col-span-5 ${
                    flip ? "lg:order-2" : "lg:order-1"
                  }`}
                >
                  <div className="glass relative overflow-hidden rounded-3xl">
                    <div className="relative aspect-[4/5] overflow-hidden">
                      <img
                        src={cat.image}
                        alt={cat.label}
                        className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                      <div
                        className="absolute inset-x-0 bottom-0 h-1"
                        style={{ backgroundColor: cat.accent }}
                      />
                      <div className="absolute bottom-6 left-6">
                        <div
                          className="overline mb-1"
                          style={{ color: cat.accent }}
                        >
                          {cat.brand}
                        </div>
                        <div className="font-display text-3xl font-light tracking-tight">
                          {cat.label}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Products list */}
                <div
                  className={`lg:col-span-7 ${
                    flip ? "lg:order-1" : "lg:order-2"
                  }`}
                >
                  <p className="mb-8 max-w-xl text-white/60 leading-relaxed lg:text-lg">
                    {cat.blurb}
                  </p>
                  <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-white/10 sm:grid-cols-2">
                    {items.map((p, i) => (
                      <motion.div
                        key={p.id}
                        custom={i}
                        variants={reveal}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                        className="group relative bg-white/[0.02] p-6 transition-colors duration-300 hover:bg-white/[0.05]"
                        data-testid={`product-card-${p.id}`}
                      >
                        <div className="mb-3 flex items-center justify-between">
                          <span
                            className="rounded-full border px-2.5 py-0.5 text-[0.65rem] uppercase tracking-wider"
                            style={{
                              color: cat.accent,
                              borderColor: `${cat.accent}55`,
                            }}
                          >
                            {p.tag}
                          </span>
                          <a
                            href={waLink(`Hi, I'd like a quote for ${p.name}.`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white/40 transition-colors duration-300 group-hover:text-white"
                            data-testid={`product-quote-${p.id}`}
                            aria-label={`Quote for ${p.name}`}
                          >
                            <ArrowUpRight className="h-4 w-4" />
                          </a>
                        </div>
                        <h4 className="font-display text-lg font-medium leading-snug tracking-tight">
                          {p.name}
                        </h4>
                        <p className="mt-2 text-sm text-white/50 leading-relaxed">
                          {p.description}
                        </p>
                        <div className="mt-4 text-xs text-white/40">
                          {p.unit}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
